// src/context/ItensContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { db } from "services/firebase";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, writeBatch } from "firebase/firestore";
import { useAuth } from "./AuthContext";

export interface Item {
  id?: string;
  nome: string;
  dataAdicao: string;
  dispositivoId: string;
  disponivel: boolean;
}

interface ItensContextType {
  itens: Item[];
  adicionarItem: (item: Omit<Item, 'id'>) => Promise<void>;
  removerItem: (id: string) => Promise<void>;
  atualizarStatusItem: (id: string, disponivel: boolean) => Promise<void>;
  fetchItens: (deviceId?: string) => Promise<void>;
  removerItensPorDispositivo: (deviceId: string) => Promise<void>;
}

const ItensContext = createContext<ItensContextType | undefined>(undefined);

export const ItensProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [itens, setItens] = useState<Item[]>([]);

  const getCollectionRef = () => {
    if (!user?.uid) return null;
    return collection(db, `clientes/${user.uid}/itens`);
  };

  const getDocRef = (id: string) => {
    if (!user?.uid) return null;
    return doc(db, `clientes/${user.uid}/itens`, id);
  };

  const fetchItens = async (deviceId?: string) => {
    const itensCollectionRef = getCollectionRef();
    if (!itensCollectionRef) {
      setItens([]);
      return;
    }

    let q = query(itensCollectionRef);
    if (deviceId) {
      q = query(itensCollectionRef, where("dispositivoId", "==", deviceId));
    }
    const data = await getDocs(q);
    setItens(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })) as Item[]);
  };

  useEffect(() => {
    if (user) {
      fetchItens();
    } else {
      setItens([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const adicionarItem = async (item: Omit<Item, 'id'>) => {
    const itensCollectionRef = getCollectionRef();
    if (!itensCollectionRef) return;
    await addDoc(itensCollectionRef, item);
    await fetchItens();
  };

  const removerItem = async (id: string) => {
    const itemDoc = getDocRef(id);
    if (!itemDoc) return;
    await deleteDoc(itemDoc);
    await fetchItens();
  };

  const removerItensPorDispositivo = async (deviceId: string) => {
    const itensCollectionRef = getCollectionRef();
    if (!itensCollectionRef) return;

    try {
      const q = query(itensCollectionRef, where("dispositivoId", "==", deviceId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) return;

      const batch = writeBatch(db);
      snapshot.docs.forEach(docSnapshot => {
        batch.delete(docSnapshot.ref);
      });
      
      await batch.commit();
      await fetchItens();
    } catch (error) {
      console.error("Erro ao remover itens por dispositivo:", error);
      throw error;
    }
  };

  const atualizarStatusItem = async (id: string, disponivel: boolean) => {
    const itemDoc = getDocRef(id);
    if (!itemDoc) return;
    await updateDoc(itemDoc, { disponivel: disponivel });
    await fetchItens();
  };

  return (
    <ItensContext.Provider
      value={{ 
        itens, 
        adicionarItem, 
        removerItem, 
        atualizarStatusItem, 
        fetchItens,
        removerItensPorDispositivo 
      }}
    >
      {children}
    </ItensContext.Provider>
  );
};

export const useItens = () => {
  const context = useContext(ItensContext);
  if (!context) {
    throw new Error("useItens precisa estar dentro de ItensProvider");
  }
  return context;
};
